import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, RadioGroup, Radio, Label, Spinner } from '@heroui/react';
import { useDmFolders } from '@/lib/useDmFolders';
import {
  useDmFolderPrefs,
  setShowTags,
  setTabPlacement,
} from '@/lib/dmFolderPrefs';
import { Switch } from '@/components/ui/Switch';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { TrashIcon, PlusIcon, EditIcon } from '@/components/icons';
import type { DmFolder } from '@/types/api';

/**
 * Telegram-style "Папки" settings hub. Manages folders (create / rename /
 * delete) and two per-device display preferences: whether each chat row shows
 * its folder tags, and where the folder tab strip sits (above vs left of the
 * chat list). Folder CRUD goes through {@link useDmFolders}; display prefs
 * through {@link useDmFolderPrefs} (localStorage, reactive).
 */
export function DmFoldersModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { folders, isLoading, renameFolder, deleteFolder, createFolder, isMutating } =
    useDmFolders();
  const prefs = useDmFolderPrefs();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  // Esc cancels: the blur that follows (the input unmounts) must not create.
  const skipBlurRef = useRef(false);
  // Enter submits, then the input is disabled and blurs: one create, not two.
  const submittingRef = useRef(false);

  function cancelNew() {
    skipBlurRef.current = true;
    setCreating(false);
    setNewName('');
  }

  async function submitNew() {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    const name = newName.trim();
    if (!name) {
      setCreating(false);
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await createFolder(name);
      setNewName('');
      setCreating(false);
    } catch {
      // useDmFolders already toasts the failure; keep the draft open so the
      // user can retry instead of losing what they typed.
      return;
    } finally {
      submittingRef.current = false;
    }
  }

  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Container className="app-panel-container" scroll="inside" size="sm">
        <Modal.Dialog className="app-panel-dialog">
          <Modal.Header className="app-panel-titlebar">
            <Modal.Heading>{t('gmod.foldersTitle')}</Modal.Heading>
            <Modal.CloseTrigger aria-label={t('common.close')} />
          </Modal.Header>
          <Modal.Body className="app-panel-body app-folder-groups flex flex-col gap-4">
            <section>
              <div className="mb-1.5 text-xs font-medium text-ink-muted">
                {t('gmod.myFolders')}
              </div>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner />
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {folders.map((f) => (
                    <FolderRow
                      key={f.id}
                      folder={f}
                      disabled={isMutating}
                      onRename={(name) => renameFolder(f.id, name)}
                      onDelete={() => deleteFolder(f.id)}
                    />
                  ))}

                  {creating ? (
                    <form
                      className="px-1 py-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void submitNew();
                      }}
                    >
                      <input
                        autoFocus
                        aria-label={t('gmod.folderNamePlaceholder')}
                        value={newName}
                        maxLength={40}
                        disabled={isMutating}
                        onChange={(e) => setNewName(e.target.value)}
                        onBlur={() => void submitNew()}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            // Cancel the draft only: don't let Esc reach the
                            // modal and close it.
                            e.preventDefault();
                            e.stopPropagation();
                            cancelNew();
                          }
                        }}
                        placeholder={t('gmod.folderNamePlaceholder')}
                        className="w-full rounded-lg bg-default/50 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-muted focus-visible:ring-2 focus-visible:ring-accent"
                      />
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        skipBlurRef.current = false;
                        setCreating(true);
                      }}
                      className="group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm text-ink transition-colors hover:bg-default/40"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-default/70 text-accent">
                        <PlusIcon className="h-4 w-4" />
                      </span>
                      {t('gmod.createNewFolder')}
                    </button>
                  )}
                </div>
              )}
            </section>



            <section className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">{t('gmod.chatTags')}</div>
                <div className="mt-0.5 text-xs text-muted">
                  {t('gmod.chatTagsHint')}
                </div>
              </div>
              <Switch
                checked={prefs.showTags}
                onChange={setShowTags}
                label={t('gmod.chatTags')}
                className="mt-0.5 shrink-0"
              />
            </section>



            <section>
              <div className="mb-2 text-xs font-medium text-ink-muted">
                {t('gmod.tabPlacement')}
              </div>
              <RadioGroup
                variant="secondary"
                aria-label={t('gmod.tabPlacement')}
                value={prefs.tabPlacement}
                onChange={(v) => setTabPlacement(v === 'left' ? 'left' : 'top')}
              >
                <Radio value="left" className="min-h-11">
                  <Radio.Control>
                    <Radio.Indicator />
                  </Radio.Control>
                  <Radio.Content>
                    <Label>{t('gmod.tabPlacementLeft')}</Label>
                  </Radio.Content>
                </Radio>
                <Radio value="top" className="min-h-11">
                  <Radio.Control>
                    <Radio.Indicator />
                  </Radio.Control>
                  <Radio.Content>
                    <Label>{t('gmod.tabPlacementTop')}</Label>
                  </Radio.Content>
                </Radio>
              </RadioGroup>
            </section>

          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

/** A single folder row: icon, name + chat count, inline rename, delete. */
function FolderRow(props: {
  folder: DmFolder;
  disabled: boolean;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [name, setName] = useState(props.folder.name);
  // Esc cancels the rename: the blur that follows must not save the draft.
  const skipBlurRef = useRef(false);
  const count = props.folder.channelIds.length;

  function startEditing() {
    skipBlurRef.current = false;
    setEditing(true);
  }

  function commit() {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    const trimmed = name.trim();
    if (trimmed && trimmed !== props.folder.name) props.onRename(trimmed);
    else setName(props.folder.name);
    setEditing(false);
  }

  return (
    <div className="group flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-default/40">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center text-primary">
        <FolderGlyph className="h-5 w-5" />
      </span>
      {editing ? (
        <input
          autoFocus
          value={name}
          maxLength={40}
          aria-label={t('gmod.folderNamePlaceholder')}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              // Cancel this rename only — Esc must not also close the modal.
              e.preventDefault();
              e.stopPropagation();
              skipBlurRef.current = true;
              setName(props.folder.name);
              setEditing(false);
            }
          }}
          className="min-w-0 flex-1 rounded-lg bg-default/50 px-2 py-1 text-sm text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      ) : (
        <button
          type="button"
          onClick={startEditing}
          className="min-w-0 flex-1 text-left"
        >
          <div className="truncate text-sm font-medium text-foreground">
            {props.folder.name}
          </div>
          <div className="truncate text-xs text-muted">
            {count > 0 ? t('gmod.chatCount', { count }) : t('gmod.noChats')}
          </div>
        </button>
      )}
      {!editing && confirmingDelete && (
        <InlineConfirm
          label={t('gmod.deleteFolderConfirm', { name: props.folder.name })}
          className="flex shrink-0 items-center gap-1"
        >
          <button
            type="button"
            disabled={props.disabled}
            onClick={() => {
              setConfirmingDelete(false);
              props.onDelete();
            }}
            className="min-h-11 rounded-lg px-3 text-sm text-danger-ink transition-colors hover:bg-danger/10 disabled:opacity-50"
          >
            {t('gmod.deleteFolderYes')}
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setConfirmingDelete(false);
              }
            }}
            className="min-h-11 rounded-lg px-3 text-sm text-muted transition-colors hover:bg-default/60 hover:text-foreground"
          >
            {t('gmod.no')}
          </button>
        </InlineConfirm>
      )}
      {!editing && !confirmingDelete && (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(pointer:coarse)]:opacity-100">
          <button
            type="button"
            aria-label={t('gmod.renameFolder')}
            disabled={props.disabled}
            onClick={startEditing}
            className="rounded-lg p-1.5 text-muted transition-colors hover:bg-default/60 hover:text-foreground disabled:opacity-50"
          >
            <EditIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t('gmod.deleteFolder')}
            disabled={props.disabled}
            onClick={() => setConfirmingDelete(true)}
            className="rounded-lg p-1.5 text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Folder glyph used in rows. */
function FolderGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <path
        d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
  );
}
